import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
    LineChart, Line, XAxis, YAxis, ReferenceLine, ResponsiveContainer, Legend, Tooltip
} from 'recharts';

import {useIsMobile} from '../../../utils/utils';
import styles from './MotionDataPanel.module.css';

const JOINT_COLORS = [
    '#5b9bd5', '#f0a04b', '#60c87a',
    '#e05555', '#9b72cf', '#c87a5b',
    'rgba(255,255,255,0.75)',
     '#46d3db', '#e66ab8'
];

const LEGEND_WRAPPER_STYLE = { fontSize: 9, color: 'rgba(255,255,255,0.35)', paddingBottom: 2 };

function formatSeconds(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds));
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function PortalTooltip({ active, payload, label, coordinate, containerRef }) {
    if (!active || !payload?.length || !coordinate) return null;

    const wrapperEl = containerRef.current?.querySelector('.recharts-wrapper');
    const rect = wrapperEl?.getBoundingClientRect() ?? { left: 0, top: 0 };
    const x = rect.left + coordinate.x + 12;
    const y = rect.top + coordinate.y - 8;

    const portalTarget = document.fullscreenElement ?? document.body;
    return ReactDOM.createPortal(
        <div className="motionPortalTooltip" style={{ left: x, top: y }}>
            <div className="motionPortalTooltipLabel">{formatSeconds(parseFloat(label))}</div>
            {payload.map(entry => (
                <div key={entry.dataKey} className="motionPortalTooltipRow" style={{ color: entry.color }}>
                    <span>{entry.name}:</span>
                    <span className="motionPortalTooltipValue">{Number(entry.value).toFixed(4)}</span>
                </div>
            ))}
        </div>,
        portalTarget
    );
}

function ArmChart({ rows, label, currentTime, onSeek, chartHeight }) {
    const containerRef = useRef(null);
    const joints = rows.length > 0 ? Object.keys(rows[0]).filter(k => k !== 't') : [];

    const handleChartClick = (chartState) => {
        if (chartState?.activeLabel == null) return;
        onSeek?.(Number(chartState.activeLabel));
    };

    return (
        <div className={styles.chartWrap} ref={containerRef}>
            <div className={styles.chartLabel}>{label}</div>
            <ResponsiveContainer width="100%" height={chartHeight}>
                <LineChart
                    data={rows}
                    margin={{ top: 2, right: 6, bottom: 0, left: 0 }}
                    onClick={handleChartClick}
                    style={{ cursor: onSeek ? 'pointer' : 'default' }}
                >
                    <XAxis
                        dataKey="t"
                        type="number"
                        domain={['dataMin', 'dataMax']}
                        tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.3)' }}
                        tickCount={5}
                        tickFormatter={formatSeconds}
                    />
                    <YAxis
                        tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.3)' }}
                        width={26}
                    />
                    <Legend
                        verticalAlign="top"
                        wrapperStyle={LEGEND_WRAPPER_STYLE}
                        iconType="plainline"
                        iconSize={12}
                    />
                    {joints.map((joint, i) => (
                        <Line
                            key={joint}
                            type="monotone"
                            dataKey={joint}
                            stroke={JOINT_COLORS[i % JOINT_COLORS.length]}
                            dot={false}
                            strokeWidth={1.2}
                            isAnimationActive={false}
                        />
                    ))}
                    {currentTime != null && (
                        <ReferenceLine
                            x={currentTime}
                            stroke="rgba(255,255,255,0.35)"
                            strokeDasharray="4 2"
                            strokeWidth={1}
                        />
                    )}
                    <Tooltip content={(props) => <PortalTooltip {...props} containerRef={containerRef} />} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

function useViewportHeight() {
    const [height, setHeight] = useState(() => window.innerHeight);
    useEffect(() => {
        function handleResize() {
            setHeight(window.innerHeight);
        }
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return height;
}

export default function MotionDataPanel({ currentTime, isOpen, data, loading, error, onSeek }) {
    const isMobile = useIsMobile(900);
    const viewportHeight = useViewportHeight();
    const chartHeight = isMobile ? 110 : Math.max(110, Math.min(195, viewportHeight * 0.22));

    return (
        <div className={`${styles.panel}${isOpen ? ` ${styles.panelOpen}` : ''}`}>
            <div className={styles.panelInner}>
                {loading && (
                    <div className={styles.statusRow}>Loading motion data…</div>
                )}
                {!loading && error && (
                    <div className={styles.statusRow}>Motion data unavailable.</div>
                )}
                {!loading && !error && data && (
                    <>
                        <ArmChart rows={data.left}  label="Left Arm"  currentTime={currentTime} onSeek={onSeek} chartHeight={chartHeight} />
                        <div className={styles.divider} />
                        <ArmChart rows={data.right} label="Right Arm" currentTime={currentTime} onSeek={onSeek} chartHeight={chartHeight} />
                    </>
                )}
            </div>
        </div>
    );
}
